'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { allowsVendorDestinations } from './consent-state';
import { useConsent } from './use-consent';

/** Guards the upgrade reload against looping. Session-scoped, like the equivalent in the Webflow head. */
const RELOAD_FLAG = '__extern_consent_reloaded';

/**
 * What this pageview was configured with, pinned once the answer lands.
 *
 * The two flags are tracked separately because they are not the same question: Segment's
 * device-mode destinations turn on for `statistics || marketing`, while PostHog's mode keys on
 * `statistics` alone. A visitor who loaded with marketing and later adds statistics never changes
 * `allowVendors`, so watching only that would leave PostHog cookieless — capturing opted out — for
 * the rest of the pageview.
 */
interface PageviewConfiguration {
  allowVendors: boolean;
  statistics: boolean;
}

/**
 * Reloads the page once, when a visitor grants consent on a pageview that was configured without
 * it. Segment cannot turn its device-mode destinations on after `analytics.load()`, and PostHog
 * cannot swap out of cookieless mode after `init()` — both are decided from the consent answer at
 * mount, so honouring a mid-session accept on the page the visitor is already looking at means
 * starting that page over.
 *
 * Three guards, because the CMP's accept event also fires on every later pageview that already
 * carries an answer: reload only if this pageview was configured without something the visitor has
 * now granted, only once per session, and not at all when sessionStorage is unavailable (private
 * mode) — there, the new answer simply takes effect on the next navigation.
 *
 * Known side effect: an accepting visitor produces two warehouse pageviews. It does not corrupt
 * attribution, which is keyed on the signup `url_params`, but it inflates their pageview count.
 */
export function useConsentUpgradeReload() {
  const $ = _c(7);
  const {
    categories,
    isResolved
  } = useConsent();
  const configuredAtLoad = useRef(null);
  let t0;
  if ($[0] !== categories) {
    t0 = allowsVendorDestinations(categories);
    $[0] = categories;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const allowVendors = t0;
  const {
    statistics
  } = categories;
  let t1;
  let t2;
  if ($[2] !== allowVendors || $[3] !== isResolved || $[4] !== statistics) {
    t1 = () => {
      if (!isResolved) {
        return;
      }
      if (configuredAtLoad.current === null) {
        configuredAtLoad.current = {
          allowVendors,
          statistics
        };
        return;
      }
      const gainedVendors = !configuredAtLoad.current.allowVendors && allowVendors;
      const gainedStatistics = !configuredAtLoad.current.statistics && statistics;
      if (!(gainedVendors || gainedStatistics)) {
        return;
      }
      try {
        if (sessionStorage.getItem(RELOAD_FLAG)) {
          return;
        }
        sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch {
        return;
      }
      window.location.reload();
    };
    t2 = [isResolved, allowVendors, statistics];
    $[2] = allowVendors;
    $[3] = isResolved;
    $[4] = statistics;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
}